import { useState, useEffect, useCallback } from 'react';
import './CommunityWorkDiscovery.css';
import { communityEventsAPI, eventsAPI, commentsAPI } from './services/api';
import CommunityEventCard from './CommunityEventCard';
import OrganizeEventDialog from './OrganizeEventDialog';
import CreateCommunityEventPanel from './CreateCommunityEventPanel';
import DangerousReportDialog from './DangerousReportDialog';

const EVENT_TYPE_TABS = [
  { key: 'community-work', label: '🔨 Community Work', value: 1 },
  { key: 'art',            label: '🎨 Art',            value: 2 },
  { key: 'music',          label: '🎵 Music',          value: 3 },
  { key: 'sport',          label: '⚽ Sport',          value: 4 },
  { key: 'education',      label: '📚 Education',      value: 6 },
];

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const EMPTY_SECTION = { items: [], totalCount: 0, loading: false };

function CommunityWorkDiscovery({ currentUser }) {
  const [activeTab, setActiveTab]   = useState(EVENT_TYPE_TABS[0].value);
  const [pageSize, setPageSize]     = useState(10);
  const [followedEvents, setFollowedEvents] = useState(new Set());

  // Per-section state: { items, totalCount, loading, page }
  const [pending,  setPending]  = useState({ ...EMPTY_SECTION, page: 1 });
  const [approved, setApproved] = useState({ ...EMPTY_SECTION, page: 1 });
  const [past,     setPast]     = useState({ ...EMPTY_SECTION, page: 1 });

  const [showGatheringDialog,      setShowGatheringDialog]      = useState(false);
  const [selectedEvent,            setSelectedEvent]            = useState(null);
  const [showDangerousDialog,      setShowDangerousDialog]      = useState(false);
  const [dangerousEvent,           setDangerousEvent]           = useState(null);
  const [showCreateCommunityEvent, setShowCreateCommunityEvent] = useState(false);
  const [locationError,            setLocationError]            = useState(null);

  const canFetch = currentUser?.homeLatitude && currentUser?.homeLongitude;

  const fetchSection = useCallback(async (setter, statuses, pastOnly, page, currentPageSize, eventType) => {
    if (!canFetch) return;
    setter(prev => ({ ...prev, loading: true }));
    try {
      const result = await communityEventsAPI.getNearby(
        currentUser.homeLatitude,
        currentUser.homeLongitude,
        currentUser.preferredRadiusKm || 10,
        statuses,
        eventType,
        false,          // includePast
        pastOnly,
        page,
        currentPageSize
      );
      setter({ items: result.items, totalCount: result.totalCount, page, loading: false });
    } catch (err) {
      console.error('Error loading section:', err);
      setter(prev => ({ ...prev, loading: false }));
    }
  }, [canFetch, currentUser]);

  const loadAll = useCallback((eventType, currentPageSize, pendingPage = 1, approvedPage = 1, pastPage = 1) => {
    fetchSection(setPending,  [0, -1], false, pendingPage,  currentPageSize, eventType);
    fetchSection(setApproved, [1],     false, approvedPage, currentPageSize, eventType);
    fetchSection(setPast,     null,    true,  pastPage,     currentPageSize, eventType);
  }, [fetchSection]);

  useEffect(() => {
    if (!currentUser) return;
    if (!canFetch) {
      setLocationError('Please set your location preferences in your Profile to discover community events in your area.');
      return;
    }
    setLocationError(null);
    setPending(prev => ({ ...prev, page: 1 }));
    setApproved(prev => ({ ...prev, page: 1 }));
    setPast(prev => ({ ...prev, page: 1 }));
    loadAll(activeTab, pageSize);
  }, [currentUser, activeTab]);

  useEffect(() => {
    if (!canFetch) return;
    setPending(prev => ({ ...prev, page: 1 }));
    setApproved(prev => ({ ...prev, page: 1 }));
    setPast(prev => ({ ...prev, page: 1 }));
    loadAll(activeTab, pageSize);
  }, [pageSize]);

  const loadFollowedEvents = useCallback(async () => {
    if (!currentUser) return;
    try {
      const followed = await communityEventsAPI.getUserFollowed(currentUser.id);
      setFollowedEvents(new Set(followed.map(e => e.id)));
    } catch (err) {
      console.error('Error loading followed events:', err);
    }
  }, [currentUser]);

  useEffect(() => { loadFollowedEvents(); }, [loadFollowedEvents]);

  const reloadAll = () => loadAll(activeTab, pageSize, pending.page, approved.page, past.page);

  // --- Navigation ---
  const handlePageChange = (setter, section, delta, statuses, pastOnly) => {
    const newPage = section.page + delta;
    const totalPages = Math.ceil(section.totalCount / pageSize);
    if (newPage < 1 || newPage > totalPages) return;
    fetchSection(setter, statuses, pastOnly, newPage, pageSize, activeTab);
  };

  // --- Event handlers ---
  const handleFollowToggle = async (eventId) => {
    const isFollowing = followedEvents.has(eventId);
    try {
      if (isFollowing) {
        await communityEventsAPI.unfollow(eventId);
        setFollowedEvents(prev => { const s = new Set(prev); s.delete(eventId); return s; });
      } else {
        await communityEventsAPI.follow(eventId);
        setFollowedEvents(prev => new Set(prev).add(eventId));
      }
      reloadAll();
    } catch (err) {
      console.error('Error toggling follow:', err);
      alert('Failed to update follow status. Please try again.');
    }
  };

  const handleVote = async (eventId, voteValue) => {
    try {
      await communityEventsAPI.vote(eventId, voteValue);
      reloadAll();
    } catch (err) {
      console.error('Error voting:', err);
      alert('Failed to vote. Please try again.');
    }
  };

  const handleJoinEvent = async (eventId) => {
    try {
      await eventsAPI.joinEvent(eventId);
      reloadAll();
    } catch (err) {
      console.error('Error joining event:', err);
      alert(err.response?.data?.message || 'Failed to join event. Please try again.');
    }
  };

  const handleCreateGathering = (event) => { setSelectedEvent(event); setShowGatheringDialog(true); };
  const handleCloseDialog = async (gatheringCreated = false) => {
    setShowGatheringDialog(false);
    setSelectedEvent(null);
    if (gatheringCreated) reloadAll();
  };
  const handleMarkAsDangerous    = (event) => { setDangerousEvent(event); setShowDangerousDialog(true); };
  const handleCloseDangerousDialog = () => { setShowDangerousDialog(false); setDangerousEvent(null); };
  const handleDangerousReportSubmitted = async () => reloadAll();
  const handleCommentVote = async (commentId, voteValue) => {
    try {
      await commentsAPI.vote(commentId, voteValue);
      reloadAll();
    } catch (err) {
      console.error('Error voting on comment:', err);
      alert('Failed to vote on comment. Please try again.');
    }
  };

  // --- Render helpers ---
  const renderCards = (items) => (
    <div className="events-grid">
      {items.map(event => (
        <CommunityEventCard
          key={event.id}
          event={event}
          currentUser={currentUser}
          showVoting={false}
          followedEvents={followedEvents}
          onFollowToggle={handleFollowToggle}
          onVote={handleVote}
          onJoinEvent={handleJoinEvent}
          onCreateGathering={handleCreateGathering}
          onMarkAsDangerous={handleMarkAsDangerous}
          onCommentVote={handleCommentVote}
        />
      ))}
    </div>
  );

  const renderPagination = (section, setter, statuses, pastOnly) => {
    const totalPages = Math.ceil(section.totalCount / pageSize);
    if (totalPages <= 1 && section.items.length === 0) return null;
    return (
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(setter, section, -1, statuses, pastOnly)}
          disabled={section.page <= 1 || section.loading}
        >
          ← Prev
        </button>
        <span className="pagination-info">
          Page {section.page} of {totalPages || 1} ({section.totalCount} total)
        </span>
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(setter, section, 1, statuses, pastOnly)}
          disabled={section.page >= totalPages || section.loading}
        >
          Next →
        </button>
      </div>
    );
  };

  const renderSection = (title, section, setter, statuses, pastOnly, emptyMsg) => (
    <div className="events-section">
      <h3 className="section-title">{title}</h3>
      {section.loading ? (
        <p className="loading-message">Loading...</p>
      ) : section.items.length === 0 ? (
        <p className="no-events-message">{emptyMsg}</p>
      ) : (
        <>
          {renderCards(section.items)}
          {renderPagination(section, setter, statuses, pastOnly)}
        </>
      )}
    </div>
  );

  if (!currentUser) {
    return (
      <div className="community-work-discovery">
        <div className="discovery-header">
          <h2>🌍 Community Events Discovery</h2>
          <p className="header-description">Please log in to discover community events in your area</p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-work-discovery">
      <div className="discovery-header">
        <h2>🌍 Community Events Discovery</h2>
        <button className="create-event-button-header" onClick={() => setShowCreateCommunityEvent(true)}>
          ➕ Create Event
        </button>
        <p className="header-description">Find and join community events in your region</p>
        {canFetch && (
          <div className="location-info">
            📍 Showing events within {currentUser.preferredRadiusKm}km of your location
          </div>
        )}
      </div>

      {locationError && (
        <div className="error-message">
          <p>{locationError}</p>
          <a href="/profile" className="set-location-link">Go to Profile to set location</a>
        </div>
      )}

      {!locationError && (
        <>
          {/* Controls row */}
          <div className="controls-row">
            <div className="tab-navigation">
              {EVENT_TYPE_TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`tab-button ${activeTab === tab.value ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="page-size-control">
              <label htmlFor="page-size-select">Per page:</label>
              <select
                id="page-size-select"
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sections */}
          {renderSection(
            'Waiting for Approval',
            pending, setPending, [0, -1], false,
            'No pending events in your area.'
          )}
          {renderSection(
            'Approved',
            approved, setApproved, [1], false,
            'No approved events in your area.'
          )}
          {renderSection(
            'Past Events',
            past, setPast, null, true,
            'No past events in your area.'
          )}
        </>
      )}

      <OrganizeEventDialog
        isOpen={showGatheringDialog}
        onClose={handleCloseDialog}
        communityEvent={selectedEvent}
        currentUser={currentUser}
      />

      {showDangerousDialog && dangerousEvent && (
        <DangerousReportDialog
          event={dangerousEvent}
          onClose={handleCloseDangerousDialog}
          onSubmitSuccess={handleDangerousReportSubmitted}
        />
      )}

      {showCreateCommunityEvent && (
        <div className="create-event-dialog-overlay" onClick={() => setShowCreateCommunityEvent(false)}>
          <div className="create-event-dialog" onClick={e => e.stopPropagation()}>
            <button className="close-dialog" onClick={() => setShowCreateCommunityEvent(false)}>×</button>
            <CreateCommunityEventPanel
              currentUser={currentUser}
              onEventCreated={() => {
                setShowCreateCommunityEvent(false);
                reloadAll();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunityWorkDiscovery;